import * as THREE from 'three'
import React, { useEffect, useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import gsap from 'gsap'

type Props = {
  fabricRef: React.RefObject<THREE.Object3D | null>
  isMobile?: boolean
  spawnIntervalMs?: number
  lifeMs?: number
  showBoundingBox?: boolean
}

export function DefectsOnFabric({
  fabricRef,
  isMobile = false,
  spawnIntervalMs,
  lifeMs = 4500,
  showBoundingBox = false
}: Props) {
  const groupRef = useRef<THREE.Group | null>(null)
  const textures = useMemo(() => {
    const loader = new THREE.TextureLoader()
    // Charge 10 défauts nommés 1..10 dans /public/defects/
    return Array.from({ length: 10 }).map((_, i) =>
      loader.load(`${process.env.PUBLIC_URL}/defects/${i + 1}.png`)
    )
    // textures shared — pas de disposal ici
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // default intervals (mobile slower spawn)
  const intervalMs = spawnIntervalMs ?? (isMobile ? 1400 : 800)

  const spawnDefect = () => {
    if (!groupRef.current) return
    if (!fabricRef?.current) return

    // Taille en fonction du device
    const size = isMobile ? 0.025 : 0.032

    const mat = new THREE.MeshBasicMaterial({
      map: textures[Math.floor(Math.random() * textures.length)],
      transparent: true,
      depthWrite: false,
      opacity: 0
    })

    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(size, size), mat)


    // Mesurer la bounding box du tissu
    const fabricBox = new THREE.Box3().setFromObject(fabricRef.current as THREE.Object3D)
    if (!fabricBox.isEmpty()) {
      const x = THREE.MathUtils.lerp(fabricBox.min.x -0.5, fabricBox.max.x - 0.8, Math.random())
      const yStart = fabricBox.max.y 
      const zStart = fabricBox.min.z + 0.15

      mesh.position.set(x, yStart, zStart)
      mesh.rotation.x = -Math.PI / 4
      mesh.rotation.z = (Math.random() - 0.5) * 0.2
      groupRef.current.add(mesh)

      // Optional: BoxHelper en debug
      let boxHelper: THREE.BoxHelper | null = null
      if (showBoundingBox) {
        boxHelper = new THREE.BoxHelper(mesh, 0xff0000)
        groupRef.current.add(boxHelper)
      }

      // cibles finales
      const yFinal =  0.7// Y- final stable (sur la face)
      const zFinal = fabricBox.max.z + 0.4 // Z+ (devant) final

      // Animation d'arrivée
      gsap.to(mesh.position, {
        y: yFinal,
        z: zFinal,
        duration:  10 ,
        ease: 'power1.inOut',
        onUpdate: () => {
          if (boxHelper) boxHelper.update()
        }
      })
      // Fade-in
      gsap.to(mat, { opacity: 1, duration: 0.2, ease: 'power1.out' })

      // Après la vie du défaut, fade-out et cleanup
      const lifeTimer = window.setTimeout(() => {
        gsap.to(mat, {
          opacity: 0,
          duration: 0.6,
          onComplete: () => {
            // retire et dispose
            try {
              groupRef.current?.remove(mesh)
              (mesh.geometry as THREE.BufferGeometry).dispose()
              if ((mat as any).map) (mat as any).map = null
              (mat as THREE.Material).dispose()
            } catch (e) {
              // ignore disposal errors
            }
            if (boxHelper && groupRef.current) {
              try {
                groupRef.current.remove(boxHelper)
                // boxHelper uses same geometry; no explicit dispose needed
                boxHelper = null
              } catch {}
            }
          }
        })
      }, lifeMs)

      // also ensure that if component unmounts quickly we clear the timer
      // we store lifeTimer id on the mesh to find & clear on unmount if needed
      ;(mesh as any).__lifeTimer = lifeTimer
    } else {
      // fabricBox empty — just place at origin fallback
      mesh.position.set((Math.random() - 0.5) * 0.6, 0.6, 0.01)
      groupRef.current.add(mesh)
      gsap.to(mat, { opacity: 1, duration: 0.4 })
      const lifeTimer = window.setTimeout(() => {
        groupRef.current?.remove(mesh)
        (mesh.geometry as THREE.BufferGeometry).dispose()
        (mat as THREE.Material).dispose()
      }, lifeMs)
      ;(mesh as any).__lifeTimer = lifeTimer
    }
  }

  useEffect(() => {
    const id = window.setInterval(spawnDefect, intervalMs)
    return () => {
      window.clearInterval(id)
      // clear any pending life timers on children
      if (groupRef.current) {
        groupRef.current.children.forEach((c: any) => {
          if (c && c.__lifeTimer) {
            window.clearTimeout(c.__lifeTimer)
          }
        })
      }
    }
    // textures are static, isMobile included to re-create interval on change
  }, [textures, isMobile, intervalMs, lifeMs])

  // small rotation/cleanup tick — keep BoxHelpers updated
  useFrame(() => {
    if (!groupRef.current) return
    groupRef.current.children.forEach((child: any) => {
      // nothing else required each frame; BoxHelpers are updated by gsap onUpdate
      // safety: if mesh got too far below fabric, remove it
      if (child.position && child.position.y < -2) {
        try {
          groupRef.current?.remove(child)
          if (child.geometry) child.geometry.dispose()
          if (child.material) child.material.dispose()
        } catch {}
      }
    })
  })

  return <group ref={groupRef} />
}